import { Absolute, Text } from 'mds-web-ui'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { ChevronUp } from 'react-feather'
import styled from 'styled-components'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import {
  color,
  flex,
  layout,
  position,
  space,
  typography,
  left
} from 'styled-system'
import { Link } from 'gatsby'
import { fontSizes } from '../utils/Typography'

const VISIBLE_CLASSNAME = 'bonobo__scrolltop--visible'

export const ScrollTop = forwardRef(
  ({ showBundleLink, appIcons, threshold = '50%', color }, ref) => {
    const containerRef = useRef()
    useImperativeHandle(ref, () => containerRef.current)
    const [ioSupported, setIoSupported] = useState(true)

    const bundleIconImage = getImage(appIcons.bundleIconImage)

    const onClick = useCallback(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }, [])

    const thresholdRef = useRef()

    useEffect(() => {
      const threshold = thresholdRef.current
      const container = containerRef.current
      if (!threshold || !container) {
        return
      }

      // Check to make sure IntersectionObserver is supported
      if (
        typeof window === 'undefined' ||
        !('IntersectionObserver' in window)
      ) {
        setIoSupported(false)
        return
      }

      // There's only one intersection to check here
      const handleIntersect = entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            container.classList.remove(VISIBLE_CLASSNAME)
          } else {
            container.classList.add(VISIBLE_CLASSNAME)
          }
        })
      }

      const options = {
        root: null,
        rootMargin: '0px'
      }

      const observer = new IntersectionObserver(handleIntersect, options)
      observer.observe(threshold)

      return () => {
        if (observer) {
          observer.disconnect()
        }
      }
    }, [setIoSupported])

    // IntersectionObserver not supported (for some reason)
    if (!ioSupported) {
      return null
    }

    return (
      <>
        <Absolute bottom={threshold} ref={thresholdRef} />

        <Container
          ref={containerRef}
          left={[showBundleLink ? 20 : 'initial', 'initial']}
        >
          {showBundleLink ? (
            <BonoboBundleLink
              fontSize={fontSizes.body}
              pl={1}
              pr={2}
              to="/bundle/"
              flex={[1, 'auto']}
            >
              <GatsbyImage image={bundleIconImage} alt="Bonobo Bundle" />
              <Text textAlign={['center', 'left']}>
                Learn about the Bonobo Bundle
              </Text>
            </BonoboBundleLink>
          ) : null}

          <ScrollToTopButton onClick={onClick} aria-label="Scroll to top">
            <ChevronUp color={color} />
          </ScrollToTopButton>
        </Container>
      </>
    )
  }
)

const Container = styled('div')(
  {
    display: 'flex',
    gap: 8,
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 999,
    transition: 'transform 240ms ease-out',
    transform: 'translateY(calc(100% + 20px))',
    [`&.${VISIBLE_CLASSNAME}`]: {
      transform: 'translateY(0)'
    }
  },
  layout,
  space,
  position,
  left
)

const BonoboBundleLink = styled(Link)(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    borderRadius: 8,
    height: 40,
    position: 'relative',
    textDecoration: 'none',
    transition: 'background 120ms ease-out',
    color: '#000',

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      borderRadius: 'inherit',
      background: '#fff',
      opacity: 0.5,
      zIndex: -1,
      transition: 'all 120ms ease-out',
      transform: 'scale(1)'
    },

    '&:hover': {
      '&::before': {
        opacity: 1
      }
    }
  },
  typography,
  color,
  layout,
  space,
  flex
)

const ScrollToTopButton = styled('button').attrs({
  type: 'button'
})({
  width: 40,
  height: 40,
  borderRadius: 8,
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  cursor: 'pointer',
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
    background: '#fff',
    opacity: 0.5,
    zIndex: -1,
    transition: 'all 120ms ease-out',
    transform: 'scale(1)'
  },

  '&:hover': {
    '&::before': {
      opacity: 1,
      transform: 'scale(1.05)'
    }
  }
})
